import React, { useState, useEffect } from 'react';
import {
  PlusCircle,
  Edit2,
  Trash2,
  X,
  Image as ImageIcon
} from 'lucide-react';
import englishService from '../services/englishService';
import RichTextEditor from '../components/RichTextEditor';

const Alert = ({ children, variant, className = '' }) => {
  const baseStyles = 'flex w-full rounded-lg border p-4';
  const variantStyles = variant === 'destructive'
    ? 'border-red-200 bg-red-50 text-red-600'
    : 'border-gray-200 bg-gray-50 text-gray-600';

  return (
    <div className={`${baseStyles} ${variantStyles} ${className}`}>
      {children}
    </div>
  );
};

const AlertDescription = ({ children, className = '' }) => (
  <div className={`text-sm ${className}`}>
    {children}
  </div>
);

// Image compression function
const compressImage = (base64Image, maxSizeKB = 400) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = base64Image;
    
    img.onload = () => {
      let width = img.width;
      let height = img.height;
      
      // Calculate ratio to resize
      const maxSize = 1000; // Max dimension (width or height)
      if (width > maxSize || height > maxSize) {
        if (width > height) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
      }
      
      // Create canvas and resize
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      
      // Start with high quality
      let quality = 0.8;
      let compressed = canvas.toDataURL('image/jpeg', quality);
      
      // Reduce quality until we get under target size
      while (compressed.length > maxSizeKB * 1024 * 1.37 && quality > 0.2) {
        quality -= 0.1;
        compressed = canvas.toDataURL('image/jpeg', quality);
      }
      
      resolve(compressed);
    };
  });
};
 
const OvenFreshUpload = () => {
  const [ovenFreshItems, setOvenFreshItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    thumbnail: '',
    contents: [],
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchOvenFreshItems();
  }, []);

  useEffect(() => {
    if (!showModal) setError('');
  }, [showModal]);

  const fetchOvenFreshItems = async () => {
    try {
      setLoading(true);
      const response = await englishService.ovenfresh.getAll();
      setOvenFreshItems(response.data);
    } catch (error) {
      setError('Failed to fetch items: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('File size should be less than 5MB');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        // Compress image before setting in state
        const compressed = await compressImage(reader.result);
        setFormData(prev => ({ ...prev, [field]: compressed }));
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing image:', error);
      setError('Error processing image. Please try a different image.');
    }
  };

  const addContentItem = () => {
    if (formData.contents.length >= 10) {
      setError('Maximum 10 content items allowed');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      contents: [
        ...prev.contents,
        { title: '', paragraph: '', image: '', tagline: '', link: '' }
      ]
    }));
  };

  const handleContentChange = (index, e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newContents = [...prev.contents];
      newContents[index][name] = value;
      return { ...prev, contents: newContents };
    });
  };

  const handleContentImage = async (index, e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      setError('File size should be less than 5MB');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        // Compress image before setting in state
        const compressed = await compressImage(reader.result);
        setFormData(prev => {
          const newContents = [...prev.contents];
          newContents[index].image = compressed;
          return { ...prev, contents: newContents };
        });
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing content image:', error);
      setError('Error processing image. Please try a different image.');
    }
  };

  const removeContentItem = (index) => {
    setFormData(prev => ({
      ...prev,
      contents: prev.contents.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const errors = [];
    
    if (!formData.title.trim()) errors.push('Title is required');
    if (!formData.thumbnail) errors.push('Thumbnail is required');
    if (formData.contents.length === 0) {
      errors.push('At least one content item is required');
    }

    // Check content sizes to avoid MongoDB limits
    let totalSize = formData.thumbnail.length;
    formData.contents.forEach(content => {
      if (content.image) {
        totalSize += content.image.length;
      }
    });

    // Roughly estimate if total data size might exceed MongoDB's limit (16MB)
    if (totalSize > 15 * 1024 * 1024) {
      errors.push('Total image size is too large. Please use smaller or fewer images.');
    }

    if (errors.length > 0) {
      setError(errors.join(', '));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');

      const formDataToSend = {
        title: formData.title.trim(),
        thumbnail: formData.thumbnail,
        contents: formData.contents.map(content => ({
          title: content.title.trim(),
          paragraph: content.paragraph.trim(),
          image: content.image,
          tagline: content.tagline.trim(),
          link: content.link?.trim() || ''
        })),
        date: new Date(formData.date).toISOString()
      };

      const response = selectedItem
        ? await englishService.ovenfresh.update(selectedItem._id, formDataToSend)
        : await englishService.ovenfresh.create(formDataToSend);

      if (response.success) {
        setSuccess(selectedItem ? 'Item updated!' : 'Item created!');
        await fetchOvenFreshItems();
        setShowModal(false);
        resetForm();
      }
    } catch (error) {
      console.error('Submission error:', error);
      setError(error.message || 'Failed to save item. Try reducing image sizes or quantity.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      title: item.title,
      thumbnail: item.thumbnail,
      contents: item.contents.map(content => ({
        title: content.title,
        paragraph: content.paragraph,
        image: content.image,
        tagline: content.tagline,
        link: content.link || ''
      })),
      date: new Date(item.date).toISOString().split('T')[0]
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await englishService.ovenfresh.delete(selectedItem._id);
      setSuccess('Item deleted!');
      fetchOvenFreshItems();
      setShowDeleteModal(false);
      setSelectedItem(null);
    } catch (error) {
      setError(error.message || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      thumbnail: '',
      contents: [],
      date: new Date().toISOString().split('T')[0]
    });
    setSelectedItem(null);
  };

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Oven Fresh Management</h1>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600"
        >
          <PlusCircle className="mr-2" size={20} /> Add New
        </button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mb-4 bg-green-50 text-green-700 border-green-200">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
        </div>
      ) : ovenFreshItems.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600">No items found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ovenFreshItems.map(item => (
            <div key={item._id} className="bg-white rounded-lg shadow-lg border border-gray-200">
              <div className="h-48 overflow-hidden">
                <img
                  src={item.thumbnail}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{item.title}</h3>
                <div className="flex justify-between items-center">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(item)}
                      className="p-2 text-sky-500 hover:bg-sky-50 rounded-full"
                    >
                      <Edit2 size={20} />
                    </button>
                    <button
                      onClick={() => { setSelectedItem(item); setShowDeleteModal(true); }}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {selectedItem ? 'Edit Item' : 'Add Item'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-sky-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thumbnail *
                  </label>
                  <div className="flex items-center space-x-4">
                    {formData.thumbnail && (
                      <img
                        src={formData.thumbnail}
                        alt="Thumbnail preview"
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    )}
                    <label className="flex flex-col items-center p-4 border-2 border-dashed rounded-lg cursor-pointer hover:border-sky-500">
                      <ImageIcon className="text-gray-400 mb-2" size={24} />
                      <span className="text-sm text-gray-600">Upload thumbnail</span>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'thumbnail')}
                        required={!formData.thumbnail}
                      />
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-sky-500"
                    required
                  />
                </div>

                <div className="border-t pt-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold">Content Items</h3>
                    <button
                      type="button"
                      onClick={addContentItem}
                      disabled={formData.contents.length >= 10}
                      className="flex items-center text-sky-500 hover:text-sky-600 disabled:opacity-50"
                    >
                      <PlusCircle className="mr-2" size={18} /> Add Content
                    </button>
                  </div>

                  {formData.contents.map((content, index) => (
                    <div key={index} className="mb-6 p-4 border rounded-lg">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-medium">Content Item {index + 1}</h4>
                        <button
                          type="button"
                          onClick={() => removeContentItem(index)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Title
                          </label>
                          <input
                            type="text"
                            name="title"
                            value={content.title}
                            onChange={(e) => handleContentChange(index, e)}
                            className="w-full p-2 border rounded"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Image
                          </label>
                          <div className="flex items-center gap-4">
                            {content.image ? (
                              <img
                                src={content.image}
                                alt={`Content ${index + 1}`}
                                className="w-20 h-20 object-cover rounded"
                              />
                            ) : (
                              <div className="w-20 h-20 bg-gray-100 rounded flex items-center justify-center">
                                <ImageIcon className="text-gray-400" size={20} />
                              </div>
                            )}
                            <label className="flex flex-col items-center p-2 border-2 border-dashed rounded cursor-pointer">
                              <input
                                type="file"
                                className="hidden"
                                accept="image/*"
                                onChange={(e) => handleContentImage(index, e)}
                              />
                              <span className="text-sm">
                                {content.image ? 'Change Image' : 'Upload Image'}
                              </span>
                            </label>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Paragraph
                          </label>
                          <RichTextEditor
                            value={[content.paragraph || '']}
                            onChange={(paragraphContent) => {
                              setFormData(prev => {
                                const newContents = [...prev.contents];
                                newContents[index].paragraph = paragraphContent.join('');
                                return { ...prev, contents: newContents };
                              });
                            }}
                            onError={setError}
                            minHeight={220}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Tagline
                          </label>
                          <input
                            type="text"
                            name="tagline"
                            value={content.tagline}
                            onChange={(e) => handleContentChange(index, e)}
                            className="w-full p-2 border rounded"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Link
                          </label>
                          <input
                            type="url"
                            name="link"
                            value={content.link}
                            onChange={(e) => handleContentChange(index, e)}
                            placeholder="https://example.com"
                            className="w-full p-2 border rounded"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : (selectedItem ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Delete Item</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{selectedItem?.title}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OvenFreshUpload;
